import type { FxRenderPacket } from "./fxRuntime";
import { mergeFxHybridDefaults } from "./fxHybridComposition";

type FxGpuUniforms = {
  intensity: number;
  progress: number;
  seed: number;
  time: number;
  amplitude: number;
  speed: number;
  opacity: number;
  resolution: [number, number];
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
const toFinite = (value: number | undefined, fallback: number): number => (Number.isFinite(value) ? Number(value) : fallback);

const resolveGhostTrailUniforms = (args: {
  packet: FxRenderPacket;
  nowMs: number;
  width: number;
  height: number;
}): FxGpuUniforms => {
  const { packet, nowMs, width, height } = args;
  const params = mergeFxHybridDefaults(packet.effectType, packet.params ?? {});
  const trailCount = clamp(Math.round(toFinite(params.trailCount, 4)), 3, 5);
  const offsetDistance = clamp(toFinite(params.offsetDistance, 48), 0, 220);
  const intensityStrength = clamp(toFinite(params.intensity, 1), 0, 2);
  const falloff = clamp(toFinite(params.trailOpacityFalloff, 0.62), 0.05, 0.99);
  const softness = clamp(toFinite(params.softness, 0.52), 0, 1);
  const rawDirection = toFinite(params.direction, 24);
  const directionNorm = ((rawDirection % 360) + 360) % 360 / 360;
  return {
    intensity: trailCount,
    progress: directionNorm,
    seed: falloff,
    time: nowMs / 1000,
    amplitude: offsetDistance * intensityStrength * 0.46,
    speed: softness,
    opacity: clamp01(packet.opacity ?? 1),
    resolution: [Math.max(1, width), Math.max(1, height)],
  };
};

export const ghostTrailBlurGpuModule = {
  id: "gpu.ghostTrailBlur",
  label: "Ghost Trail Blur",
  kind: "replace",
  webglFragment: `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_texture;
uniform vec2 u_resolution;
uniform float u_progress;
uniform float u_seed;
uniform float u_intensity;
uniform float u_amp;
uniform float u_speed;
void main() {
  vec4 baseSharp = texture2D(u_texture, v_uv);
  float count = clamp(floor(u_intensity + 0.5), 1.0, 5.0);
  float falloff = clamp(u_seed, 0.05, 0.99);
  float softness = clamp(u_speed, 0.0, 1.0);
  float angle = u_progress * 6.2831853;
  vec2 dir = normalize(vec2(cos(angle), sin(angle)));
  vec2 normal = vec2(-dir.y, dir.x);
  vec2 px = vec2(1.0 / u_resolution.x, 1.0 / u_resolution.y);
  float pxStep = max(0.0, u_amp) / max(u_resolution.x, u_resolution.y) * (0.4 + softness * 0.24);
  float baseBlur = pxStep * (2.8 + softness * 6.2) + (0.0012 + softness * 0.0025);

  vec3 base0 = baseSharp.rgb;
  vec3 base1 = texture2D(u_texture, v_uv + normal * baseBlur).rgb;
  vec3 base2 = texture2D(u_texture, v_uv - normal * baseBlur).rgb;
  vec3 base3 = texture2D(u_texture, v_uv + dir * baseBlur).rgb;
  vec3 base4 = texture2D(u_texture, v_uv - dir * baseBlur).rgb;
  vec3 base5 = texture2D(u_texture, v_uv + vec2(baseBlur, baseBlur)).rgb;
  vec3 base6 = texture2D(u_texture, v_uv + vec2(-baseBlur, baseBlur)).rgb;
  vec3 base7 = texture2D(u_texture, v_uv + vec2(baseBlur, -baseBlur)).rgb;
  vec3 base8 = texture2D(u_texture, v_uv + vec2(-baseBlur, -baseBlur)).rgb;
  vec3 baseSoft = (
    base0 * 0.2 +
    base1 + base2 + base3 + base4 +
    base5 * 0.9 + base6 * 0.9 + base7 * 0.9 + base8 * 0.9
  ) / 7.8;

  vec3 accum = mix(base0, baseSoft, 0.9 + softness * 0.1);
  float weight = 1.0;
  for (int i = 1; i <= 5; i++) {
    float fi = float(i);
    float enabled = step(fi, count + 0.001);
    float t = fi / max(count, 1.0);
    vec2 offset = dir * pxStep * fi * (0.38 + softness * 0.22);
    float blur = pxStep * fi * (2.0 + softness * 5.0) + baseBlur * 0.58;
    vec3 sampleA = texture2D(u_texture, v_uv - offset).rgb;
    vec3 sampleB = texture2D(u_texture, v_uv - offset + normal * blur).rgb;
    vec3 sampleC = texture2D(u_texture, v_uv - offset - normal * blur).rgb;
    vec3 sampleD = texture2D(u_texture, v_uv - offset + dir * blur * 0.55).rgb;
    vec3 sampleE = texture2D(u_texture, v_uv - offset - dir * blur * 0.55).rgb;
    vec3 ghost = (sampleA * 0.22 + sampleB + sampleC + sampleD + sampleE) / 4.22;
    ghost = mix(ghost, vec3(1.0), 0.34 + t * 0.54);
    float alpha = pow(falloff, fi * 0.85) * (0.84 - t * 0.2) * (0.76 + softness * 0.26);
    accum += ghost * alpha * enabled;
    weight += alpha * enabled;
  }
  vec3 outColor = accum / max(weight, 0.0001);
  vec3 finalSoft = (
    outColor * 0.28 +
    texture2D(u_texture, v_uv + normal * baseBlur * 0.8).rgb +
    texture2D(u_texture, v_uv - normal * baseBlur * 0.8).rgb +
    texture2D(u_texture, v_uv + dir * baseBlur * 0.8).rgb +
    texture2D(u_texture, v_uv - dir * baseBlur * 0.8).rgb
  ) / 4.28;
  outColor = mix(outColor, finalSoft, 0.46 + softness * 0.24);
  outColor = mix(outColor, vec3(1.0), softness * 0.08);
  gl_FragColor = vec4(outColor, baseSharp.a);
}`,
  resolveUniforms: resolveGhostTrailUniforms,
} as const;
